import { module } from 'modujs';

export default class extends module {
    constructor(m) {
        super(m);
        this.events = { click: { toggleMenu: 'toggleMenu' } };
        this._stack = [];
        this._raf = null;
        this._contactDrop = null;
    }

    init() {
        this.menuMobile = document.querySelector('.menuMobile');
        this.menuToggle = this.el.querySelector('.c-navbar_menuToggle');
        this._default = this.el.getAttribute('data-style') || 'transparent';
        //this._setupMobileSubmenus();
        this._bindMobileLinkToggles();
        this._initContactDrop();

        this._onMarker = (e) => {
            const { target, way } = e.detail || {};
            if (!target) return;

            const container = target.closest?.('[data-load-container]');
            if (container && container.classList.contains('is-old')) return;

            if (way === 'enter') {
                if (!this._stack.includes(target)) this._stack.push(target);
            } else if (way === 'leave') {
                const i = this._stack.indexOf(target);
                if (i > -1) this._stack.splice(i, 1);
            }
            this._applyFromStack();
        };
        window.addEventListener('updateNavbarStyle', this._onMarker);

        this._applyInitial();

        this._onScroll = () => {
            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                this._applyFromStack();
            });
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onScroll);
        window.addEventListener('load', () => this._applyFromStack());
    }

    // ---------- Helpers responsive ----------
    _isMobile() {
        // aligne avec le breakpoint du menu (<= 1195px)
        return window.matchMedia('(max-width: 1195px)').matches;
    }

    // ---------- Style navbar ----------
    _styleAtScrollTop() {
        const scrollY = window.scrollY || 0;
        const markers = Array.from(document.querySelectorAll('.c-navbar-style'));
        if (!markers.length) return null;
        let current = null;
        let bestTop = -Infinity;
        for (const el of markers) {
            const top = el.getBoundingClientRect().top + scrollY;
            if (top <= scrollY + 1 && top >= bestTop) {
                current = el;
                bestTop = top;
            }
        }
        return current ? current.getAttribute('data-style') : null;
    }

    _applyInitial() {
        const style = this._styleAtScrollTop() || this._default;
        this._set(style);
    }

    _applyFromStack() {
        const visible = this._stack.slice();
        let style;

        if (visible.length) {
            visible.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
            const pick = visible[0];
            style = (pick && pick.getAttribute('data-style')) || this._default;
        } else {
            style = this._styleAtScrollTop() || this._default;
        }
        this._set(style);
    }

    _set(style) {
        if (this.el.getAttribute('data-style') !== style) {
            this.el.setAttribute('data-style', style);
        }
    }

    // ---------- Dropdown contact (Book Demo) ----------
    _initContactDrop() {
        const wrap = this.el.querySelector('.c-navbar_contactWrap[data-module-contact-drop]');
        if (!wrap) return;

        const triggers = Array.from(wrap.querySelectorAll('.js-contactDrop_trigger'));
        const drop = wrap.querySelector('.c-contactDrop');
        if (!triggers.length || !drop) return;

        const setExpanded = (expanded) => {
            triggers.forEach((t) => t.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
        };

        const close = () => {
            wrap.classList.remove('-open');
            setExpanded(false);
        };

        const open = () => {
            wrap.classList.add('-open');
            setExpanded(true);
        };

        const toggle = () => {
            const isOpen = wrap.classList.contains('-open');
            if (isOpen) close();
            else open();
        };

        const onTriggerClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle();
        };

        let closeTimer = null;
        const onWrapEnter = () => {
            if (closeTimer) {
                clearTimeout(closeTimer);
                closeTimer = null;
            }
            open();
        };
        const onWrapLeave = () => {
            closeTimer = setTimeout(close, 150);
        };

        const onDocumentClick = (e) => {
            if (!wrap.classList.contains('-open')) return;
            if (wrap.contains(e.target)) return;
            close();
        };

        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (!wrap.classList.contains('-open')) return;
            close();
            triggers[0]?.focus?.();
        };

        triggers.forEach((t) => t.addEventListener('click', onTriggerClick));
        wrap.addEventListener('mouseenter', onWrapEnter);
        wrap.addEventListener('mouseleave', onWrapLeave);
        document.addEventListener('click', onDocumentClick);
        wrap.addEventListener('keydown', onKeyDown);

        this._contactDrop = {
            wrap,
            triggers,
            onTriggerClick,
            onWrapEnter,
            onWrapLeave,
            onDocumentClick,
            onKeyDown,
            close,
            closeTimer: () => closeTimer,
            setCloseTimer: (t) => { closeTimer = t; },
        };
    }

    // ---------- Sous-menus mobile ----------
    _setupMobileSubmenus() {
        const nav = this.menuMobile && this.menuMobile.querySelector('.mainMenu');
        if (!nav) return;

        nav.querySelectorAll('li.menu-item-has-children').forEach(li => {
            // bouton séparé (optionnel : on garde)
            if (!li.querySelector('.js-mm-toggle')) {
                const a = li.querySelector(':scope > a');
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'mm-toggle js-mm-toggle';
                btn.setAttribute('aria-expanded', 'false');
                btn.setAttribute('aria-label', 'Ouvrir le sous-menu');
                btn.innerHTML = '▶';
                a && a.after(btn);

                btn.addEventListener('click', () => this._toggleOne(li));
            }
        });
    }

    _bindMobileLinkToggles() {
        const nav = this.menuMobile && this.menuMobile.querySelector('.mainMenu');
        if (!nav) return;

        nav.querySelectorAll('li.menu-item-has-children > a').forEach(a => {
            const li = a.parentElement;

            const onPointerFirst = (e) => {
                // Seulement en mobile ET quand le menu latéral est ouvert
                if (!this._isMobile() || !this.menuMobile.classList.contains('-visible')) return;

                // Premier tap => on ouvre le sous-menu, PAS de navigation
                if (!li.classList.contains('-open')) {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

                    this._toggleOne(li, true);

                    // on marque pour avaler le click synthétique qui suit
                    a.dataset.swallowClick = '1';
                }
                // Si déjà ouvert, on ne bloque pas ici : le prochain click naviguera.
            };

            const onClick = (e) => {
                // Avaler le click synthétique juste après l’ouverture
                if (a.dataset.swallowClick === '1') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
                    delete a.dataset.swallowClick;
                }
            };

            // On écoute en capture, non-passif, pour pouvoir preventDefault avant tout le monde
            ['pointerdown', 'touchstart'].forEach(type => {
                a.addEventListener(type, onPointerFirst, { capture: true, passive: false });
            });
            a.addEventListener('click', onClick, { capture: true, passive: false });
        });
    }



    _toggleOne(li, closeSiblings = false) {
        if (closeSiblings) this._closeSiblings(li);
        const open = li.classList.toggle('-open');
        const btn = li.querySelector('.js-mm-toggle');
        if (btn) {
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
            btn.setAttribute('aria-label', open ? 'Fermer le sous-menu' : 'Ouvrir le sous-menu');
        }
    }

    _closeSiblings(li) {
        const parent = li.parentElement;
        if (!parent) return;
        parent.querySelectorAll(':scope > li.menu-item-has-children.-open').forEach(sib => {
            if (sib === li) return;
            sib.classList.remove('-open');
            const b = sib.querySelector('.js-mm-toggle');
            if (b) {
                b.setAttribute('aria-expanded', 'false');
                b.setAttribute('aria-label', 'Ouvrir le sous-menu');
            }
        });
    }

    _resetMobileSubmenus() {
        const nav = this.menuMobile && this.menuMobile.querySelector('.mainMenu');
        if (!nav) return;
        nav.querySelectorAll('li.menu-item-has-children.-open').forEach(li => {
            li.classList.remove('-open');
            const b = li.querySelector('.js-mm-toggle');
            if (b) {
                b.setAttribute('aria-expanded', 'false');
                b.setAttribute('aria-label', 'Ouvrir le sous-menu');
            }
        });
    }

    // ---------- API publique existante ----------
    hide() { this.el.classList.add('-hidden'); }
    show() { this.el.classList.remove('-hidden'); }
    setStyle(style) { this._set(style); }
    setLight() { this._set('light'); }
    resetStyle() { this._set('transparent'); }

    toggleMenu() {
        if (this.menuMobile.classList.contains('-visible')) {
            this.call('start', 'Scroll');
            this.menuMobile.classList.remove('-visible');
            this.menuToggle.classList.remove('-active');
            this.el.classList.remove('-menu-open');
            this._resetMobileSubmenus();
        } else {
            this.call('stop', 'Scroll');
            this.menuMobile.classList.add('-visible');
            this.menuToggle.classList.add('-active');
            this.el.classList.add('-menu-open');
        }
    }

    destroy() {
        window.removeEventListener('updateNavbarStyle', this._onMarker);
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onScroll);

        if (this._contactDrop) {
            const {
                wrap,
                triggers,
                onTriggerClick,
                onWrapEnter,
                onWrapLeave,
                onDocumentClick,
                onKeyDown,
                close,
                closeTimer,
                setCloseTimer,
            } = this._contactDrop;
            const timer = closeTimer?.();
            if (timer) clearTimeout(timer);
            setCloseTimer?.(null);
            close?.();
            triggers?.forEach((t) => t.removeEventListener('click', onTriggerClick));
            wrap?.removeEventListener('mouseenter', onWrapEnter);
            wrap?.removeEventListener('mouseleave', onWrapLeave);
            document.removeEventListener('click', onDocumentClick);
            wrap?.removeEventListener('keydown', onKeyDown);
            this._contactDrop = null;
        }
    }
}
